import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit,
  Output,
} from '@angular/core';
import {Howl} from 'howler';
import {Moment} from 'moment';
import {BaseView} from '../../../../../../core/view/base.view';
import {MomentAdapter} from '../../../../../../../shared/utils/moment.utils';
import {AudioRecordingService} from "../../service/audioRecording.service";
import {DialogController} from "../../../../../dialog/dialogController";
import {
  AudioRecordingSettingsDialogComponent
} from "../../../../recording/api/component/audio-recording-settings-dialog/audio-recording-settings-dialog.component";

@Component({
  selector: 'app-audio-recording',
  templateUrl: './audio-recording.component.html',
})
export class AudioRecordingComponent extends BaseView implements OnDestroy, AfterViewInit {
  @Input() startRecordingOnInit = false;
  @Input() startRecordingManually = false;
  @Input() startRecordingIcon = 'microphone';
  @Input() startRecordingTooltip = 'Démarrer l\'audio';
  @Input() stopRecordingTooltip = 'Arrêter l\'audio';
  @Input() stopRecordingManually = false;
  @Input() buttonColor : 'primary' | 'white' = 'primary';
  @Input() buttonsFill: 'clear' | 'solid' = 'clear';
  @Input() maxRecordDurationInSeconds: number;
  @Input() stopOnSilenceDurationInMs: number;
  @Input() disabled = false;
  @Input() partnerId: number;
  @Input() audioRecordDeletable = false;
  @Input() enableSettings = false;
  @Input() hideButtons = false;
  @Input() hideDuration = false;

  @Output() audioRecordStartRequestedEvent = new EventEmitter<void>();
  @Output() audioRecordStartedEvent = new EventEmitter<void>();
  @Output() audioRecordStopEvent = new EventEmitter<void>();
  @Output() audioRecordedEvent = new EventEmitter<AudioRecord>();
  @Output() audioChunkRecordedEvent = new EventEmitter<Blob>();
  @Output() durationUpdatedEvent = new EventEmitter<Moment>();

  allSilence = true;
  label = undefined;
  recordStart: Moment;
  record: AudioRecord;
  error = false;
  mediaRecorder: MediaRecorder;
  mediaStream: MediaStream;
  audioChunks: Blob[] = [];

  // Automatic stop management
  stopTimeout: any;

  // Automatic stop on silence management
  silenceTimeout: any; // Pour détecter le silence
  silenceThreshold = 0.01; // Seuil de volume pour le silence (à ajuster)

  constructor(public audioRecordingService: AudioRecordingService,
              private dialogController: DialogController) {
    super();
  }

  ngAfterViewInit() {
    if (this.startRecordingOnInit) {
      this.internalStartRecording();
    }
  }

  ngOnDestroy() {
    this.stopRecording(false);
  }

  internalStartRecording() {
    if (! this.startRecordingManually) {
      this.startRecording();
    } else {
      this.audioRecordStartRequestedEvent.emit();
    }
  }
  /**
   * Start recording.
   */
  startRecording() {
    console.log('startRecording', this.audioRecordingService.recordingInProgress, this.recordStart)
    if (this.audioRecordingService.recordingInProgress || this.recordStart) {
      this.audioRecordingService.recordingInProgress = true;
      return;
    }

    try {
      this.audioRecordingService.recordingInProgress = true;
      navigator.mediaDevices.getUserMedia({audio: {
          echoCancellation: true,
          noiseSuppression: true
        }}).then((stream) => {
        const options = { audioBitsPerSecond: 16000, sampleRate: 44100 };
        this.mediaStream = stream;
        this.mediaRecorder = new MediaRecorder(stream, options);

        // Silence management
        let analyser;
        if (this.stopOnSilenceDurationInMs > 0) {
          const audioContext = new AudioContext();
          const source = audioContext.createMediaStreamSource(stream)
          analyser = audioContext.createAnalyser();
          source.connect(analyser);
          analyser.fftSize = 2048;
        }

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            // console.log(['ondataavailable', event.data]);
            this.audioChunks.push(event.data);
            this.audioChunkRecordedEvent.emit(event.data);
            const moment = MomentAdapter.of(MomentAdapter.current().get().diff( this.recordStart)).get();
            this.label = moment.format('mm:ss');
            this.durationUpdatedEvent.emit(moment);
          }
        };
        this.mediaRecorder.onstart = () => {
            this.audioRecordStartedEvent.emit();
          if (this.maxRecordDurationInSeconds) {
            this.stopTimeout = setTimeout(() => this.stopRecording(true), this.maxRecordDurationInSeconds * 1000);
          }
          if (analyser) {
            this.monitorSilence(analyser);
          }
        };
        this.mediaRecorder.onstop = () => {
          console.log(['onstop', this.audioChunks.length]);
          this.clearStopTimeout();
          this.clearSilenceTimeout();
          this.mediaStream = null;
          this.recordStart = null;
          this.label = null;
          this.durationUpdatedEvent.emit(undefined);
        };
        this.startMediaRecorder();
      })
      .catch((error) => {
        this.toastControl.presentError(error, 'Erreur lors de l\'ouverture du micro');
      });
    } catch (e) {
      this.toastControl.presentError(e, 'Aucun micro détecté');
    }
  }

  private startMediaRecorder() {
    if (this.mediaRecorder) {
      new Howl({
        src: ['assets/sound/speechOn.wav'],
        onend: () => {
          this.recordStart = MomentAdapter.current().get();
          this.audioChunks = [];
          this.mediaRecorder?.start(250);
        }
      }).play();
    }
  }


  private monitorSilence(analyser: AnalyserNode) {
    this.allSilence = true;
    const dataArray = new Uint8Array(analyser.fftSize);
    const checkSilence = () => {
      analyser.getByteTimeDomainData(dataArray);

      // Calculer l'amplitude moyenne pour déterminer le volume
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const value = (dataArray[i] / 128) - 1; // Normalisation de -1 à 1
        sum += value * value;
      }
      const averageAmplitude = Math.sqrt(sum / dataArray.length);

      // console.log('averageAmplitude ' + averageAmplitude);
      if (averageAmplitude < this.silenceThreshold) {
        // console.log('Silence détecté');
        // Si le silence dure plus de X secondes.
        if (!this.silenceTimeout) {
          this.silenceTimeout = setTimeout(() => this.stopRecording(true), this.stopOnSilenceDurationInMs);
        }
      } else {
        this.allSilence = false;
        this.clearSilenceTimeout();
      }

      // Continuer la surveillance tant que l'enregistrement est en cours
      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        requestAnimationFrame(checkSilence);
      }
    };

    checkSilence();
  }

  private clearSilenceTimeout() {
    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
      this.silenceTimeout = null;
    }
  }

  /**
   * Stop recording.
   */
  stopRecording(saveRecord: boolean) {
    this.audioRecordingService.recordingInProgress = false;
    if (this.mediaRecorder) {
      if (this.mediaRecorder.state === 'recording') {
        new Howl({
          src: ['assets/sound/beep.wav'],
        }).play();
        this.mediaRecorder.stop();
        if (saveRecord) {
          this.record = this.buildRecord();
          this.audioRecordedEvent.emit(this.record);
        }
      }
      this.mediaRecorder = null;
    }

    // console.log('Checks if mediaStream is null');
    // Arrêter les tracks indépendamment de l'état du MediaRecorder
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (error) {
          console.error('Erreur lors de l’arrêt du track', error);
        }
      });
      this.mediaStream = null;
    }

    this.clearStopTimeout();
    this.clearSilenceTimeout();
    this.recordStart = undefined;
    this.audioRecordStopEvent.emit();
  }

  openSettings() {
    this.dialogController.open(AudioRecordingSettingsDialogComponent, undefined, {
      title: 'Paramétrage du micro'
    });
  }

  private clearStopTimeout() {
    if (this.stopTimeout) {
      clearTimeout(this.stopTimeout);
      this.stopTimeout = null;
    }
  }

  private buildRecord(): AudioRecord {
    const current = MomentAdapter.current();
    return {
      name: current.format('YYYY-MM-DD-HH-mm-ss'),
      content: new Blob(this.audioChunks, {type: 'audio/wav'}),
      mimeType: 'audio/wav',
      msDuration: current.get().diff(this.recordStart, 'ms'),
      recordedAt: current.get(),
      partnerId: this.partnerId,
      deletable: this.audioRecordDeletable,
      contentValues: [],
      allSilence: this.allSilence
    };
  }
}

export class AudioRecord {
  name: string;
  recordedAt: Moment;
  content: Blob;
  msDuration: number;
  mimeType: string;
  contentValues: string[];
  deletable: boolean;
  partnerId: number;
  allSilence: boolean;
}
